
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCommissionDeliveries, createCommissionDelivery, updateCommissionDelivery } from "@/services/commission.service";
import { CommissionDelivery } from "@/types/commission.types";
import { useState } from "react";

// Get commission deliveries for a request
export const useCommissionDeliveries = (requestId: string) => {
  return useQuery({
    queryKey: ['commission-deliveries', requestId],
    queryFn: () => getCommissionDeliveries(requestId),
    enabled: !!requestId,
    retry: 1
  });
};

// Create a commission delivery
export const useCreateCommissionDelivery = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (delivery: Omit<CommissionDelivery, 'id' | 'created_at'>) => {
      return createCommissionDelivery(delivery);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['commission-deliveries', variables.commission_request_id] });
    }
  });
};

// Update a commission delivery
export const useUpdateCommissionDelivery = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, delivery }: { id: string, delivery: Partial<CommissionDelivery> }) => 
      updateCommissionDelivery(id, delivery),
    onSuccess: (data, variables) => {
      // We need to invalidate the deliveries query, but we don't have the request ID here
      // So we'll just invalidate all delivery queries
      queryClient.invalidateQueries({ queryKey: ['commission-deliveries'] });
    }
  });
};

// Mock file upload hook - replace with actual implementation later
export const useUploadDeliveryImage = () => {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const uploadImage = async (file: File): Promise<string> => {
    setIsUploading(true);
    setProgress(0);
    
    // Mock upload progress simulation
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + 10;
          if (newProgress >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              setIsUploading(false);
              // Return a mock URL - this should be replaced with actual upload implementation
              resolve(`https://example.com/uploads/${file.name}`);
            }, 500);
            return 100;
          }
          return newProgress;
        });
      }, 300);
    });
  };
  
  return {
    uploadImage,
    isUploading,
    progress
  };
};
